/**
 * What's On card (web).
 *
 * ONE card renders both kinds of thing in the feed:
 *   • a one-off `events` row  ("Fiona Young Live Cabaret, Sat 29 Aug")
 *   • an occurrence expanded from a `venue_activities` pattern
 *     ("Karaoke, every Saturday")
 *
 * They are different records with different lifecycles — that separation is
 * the point of the whole rewrite — but to someone deciding what to do on
 * Saturday they are the same question, so they share a silhouette and a
 * normalised `WhatsOnEntry` shape. The recurrence badge is the only visible
 * tell, and it is genuinely useful information, not a data-model leak.
 *
 * Layout follows StoreBrowseCard: fixed image ratio, near-constant body
 * height, so BROWSE_GRID can put 4 across at 1440 without any card growing.
 * Imagery is kept — this is a discovery surface, and the audit's own usage
 * numbers (events: 162 views, nightlife: 145) say people browse it visually.
 */
import { Link } from "wouter";
import {
  CalendarDays, Circle, CircleDot, Clock, HelpCircle, MapPin, Mic, Mic2,
  Music, PartyPopper, Repeat, Target, Trophy, Tv, Users, Utensils,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Chip } from "./primitives";
import { ELEVATION, FOCUS_RING, RADII, TYPE, categoryTone } from "./tokens";
import { dateBadgeParts, formatTime, DAY_NAMES_FULL } from "./dates";

/** The feed's normalised item. Built in WhatsOn.tsx from two API sources. */
export interface WhatsOnEntry {
  /** Stable React key. Event ids and activity occurrence ids never collide. */
  key: string;
  kind: "event" | "activity";
  href: string;
  title: string;
  summary: string | null;
  category: string | null;
  /** YYYY-MM-DD. Always a real date — never a bucket name. */
  date: string;
  /** Raw `event_time` / `startTime`. May be free text; formatTime copes. */
  time: string | null;
  imageUrl: string | null;
  town: string | null;
  venueName: string | null;
  /** null when the event has no venue entity (a park, a civic hall, nothing). */
  venueHref: string | null;
  priceText: string | null;
  isFree: boolean;
  featured?: boolean;
  /** e.g. "Every Saturday" — recurring activities only. */
  recurrence?: string | null;
}

/* ── Category glyphs ──────────────────────────────────────────────────────
   Matched case-insensitively against the messy live vocabulary. The audit
   (§2.3) found five incompatible category lists across the frontend and 3
   values present in no table, so this map is forgiving by design and falls
   back to a calendar rather than rendering nothing.                        */

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "live music": Music, music: Music, "tribute acts": Music, comedy: PartyPopper,
  karaoke: Mic, "open mic": Mic2, "quiz night": HelpCircle, quiz: HelpCircle,
  "darts night": Target, darts: Target, "pool night": CircleDot, pool: CircleDot,
  sport: Trophy, sports: Trophy, "ufc / boxing": Tv, nightlife: Music,
  "food & drink": Utensils, family: Users, children: Users, community: Users,
  charity: Users, markets: MapPin, seasonal: PartyPopper, festivals: PartyPopper,
};

export function categoryIcon(category: string | null | undefined): LucideIcon {
  if (!category) return CalendarDays;
  return CATEGORY_ICONS[category.trim().toLowerCase()] ?? CalendarDays;
}

/** "Every Saturday" / "Every other Friday" / "Monthly on Sunday" / "Always on". */
export function recurrenceLabel(frequency: string, dayOfWeek: number | null): string | null {
  const day = dayOfWeek != null ? DAY_NAMES_FULL[dayOfWeek] : null;
  switch (frequency) {
    case "weekly": return day ? `Every ${day}` : "Weekly";
    case "fortnightly": return day ? `Every other ${day}` : "Fortnightly";
    case "monthly": return day ? `Monthly on ${day}` : "Monthly";
    case "permanent": return "Always on";
    case "specific": return "Selected dates";
    default: return null;
  }
}

/* ── Date tile ────────────────────────────────────────────────────────────
   A real calendar tile, not a pill. It is the single most load-bearing piece
   of information on a date-led page and deserves a shape of its own.       */

function DateTile({ date, onImage }: { date: string; onImage?: boolean }) {
  const parts = dateBadgeParts(date);
  if (!parts) return null;
  return (
    <div
      className={cn(
        RADII.sm,
        "w-11 shrink-0 overflow-hidden text-center border",
        onImage
          ? "border-white/25 shadow-[0_2px_10px_rgba(15,23,42,0.35)]"
          : "border-slate-200",
      )}
    >
      <div className="bg-[#1e3a5f] py-0.5">
        <span className={cn(TYPE.overline, "text-white")}>{parts.month}</span>
      </div>
      <div className="bg-white py-1 leading-none">
        <span className="block text-[17px] font-bold text-slate-900 leading-none">{parts.day}</span>
        <span className="block text-[9px] font-semibold text-slate-400 uppercase mt-0.5">{parts.weekday}</span>
      </div>
    </div>
  );
}

/* ── Card ─────────────────────────────────────────────────────────────── */

export function WhatsOnCard({ entry, className }: { entry: WhatsOnEntry; className?: string }) {
  const Icon = categoryIcon(entry.category);
  const time = formatTime(entry.time);
  const priceLabel = entry.isFree ? "Free" : entry.priceText?.trim() || null;

  return (
    <Link
      href={entry.href}
      className={cn("group block h-full", FOCUS_RING, RADII.md, className)}
      aria-label={[entry.title, entry.venueName, entry.town].filter(Boolean).join(", ")}
    >
      <article
        className={cn(
          RADII.md, ELEVATION.sm,
          "h-full flex flex-col overflow-hidden bg-white border transition-all duration-200",
          "hover:shadow-[0_4px_16px_rgba(15,23,42,0.12)] md:hover:-translate-y-0.5",
          entry.featured ? "border-amber-300" : "border-slate-200/80",
        )}
      >
        {/* Image. Fixed ratio so a row of cards lines up and the skeleton can
            reserve the identical box. Shallower on phone for the same reason
            Order Food uses 21/9 there: a 16/10 hero at 390px eats the fold. */}
        <div className="relative aspect-[21/9] sm:aspect-[16/10] bg-slate-100 overflow-hidden">
          {entry.imageUrl ? (
            <img
              src={entry.imageUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 md:group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f]">
              <Icon size={26} className="text-white/35" aria-hidden />
            </div>
          )}

          <div
            className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/50 to-transparent pointer-events-none"
            aria-hidden
          />

          <div className="absolute top-2 left-2">
            <DateTile date={entry.date} onImage />
          </div>

          <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
            {entry.featured && <Chip variant="overlay" size="xs">Featured</Chip>}
            {entry.recurrence && (
              <Chip variant="overlay" size="xs" icon={<Repeat size={10} aria-hidden />}>
                {entry.recurrence}
              </Chip>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col gap-1.5 p-3">
          <h3 className={cn(TYPE.headline, "text-slate-900 line-clamp-2")}>{entry.title}</h3>

          {/* Venue line. Plain text, not a pill — pills are reserved for
              interactive filters and status. Where a venue entity exists the
              user can jump to it; where it does not (a park, a civic hall)
              the name still reads normally instead of looking broken. */}
          <p className={cn(TYPE.subhead, "text-slate-500 line-clamp-1")}>
            {entry.venueName ? (
              <span className="text-slate-600 font-medium">{entry.venueName}</span>
            ) : null}
            {entry.venueName && entry.town ? " · " : null}
            {entry.town}
          </p>

          <div className="mt-auto flex items-center gap-2 pt-1.5 flex-wrap">
            {time && (
              <span className={cn(TYPE.footnote, "inline-flex items-center gap-1 text-slate-500")}>
                <Clock size={11} aria-hidden /> {time}
              </span>
            )}
            {entry.category && (
              <Chip tone={categoryTone(entry.category)} size="xs" icon={<Icon size={10} aria-hidden />}>
                {entry.category}
              </Chip>
            )}
            {priceLabel && (
              <span
                className={cn(
                  TYPE.caption, "ml-auto",
                  entry.isFree ? "text-emerald-700" : "text-slate-700",
                )}
              >
                {priceLabel}
              </span>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}

export function WhatsOnCardSkeleton() {
  return (
    <div className={cn(RADII.md, ELEVATION.sm, "overflow-hidden bg-white border border-slate-200/80")}>
      <div className="aspect-[21/9] sm:aspect-[16/10] skeleton-shimmer bg-slate-200/70" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 w-3/4 rounded skeleton-shimmer bg-slate-200/70" />
        <div className="h-3 w-1/2 rounded skeleton-shimmer bg-slate-200/70" />
        <div className="h-3 w-1/3 rounded skeleton-shimmer bg-slate-200/70" />
      </div>
    </div>
  );
}

/**
 * Compact row — used inside VenueDetail's "What's On here" list, where the
 * venue is already established by the page and repeating its photo on every
 * row would be noise.
 */
export function WhatsOnRow({ entry }: { entry: WhatsOnEntry }) {
  const Icon = categoryIcon(entry.category);
  const time = formatTime(entry.time);
  const priceLabel = entry.isFree ? "Free" : entry.priceText?.trim() || null;

  return (
    <Link
      href={entry.href}
      className={cn(
        RADII.md, FOCUS_RING,
        "group flex items-center gap-3 bg-white border border-slate-200/80 p-3 transition-colors hover:border-slate-300",
      )}
    >
      <DateTile date={entry.date} />
      <div className="min-w-0 flex-1">
        <p className={cn(TYPE.headline, "text-slate-900 line-clamp-1")}>{entry.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {time && (
            <span className={cn(TYPE.footnote, "inline-flex items-center gap-1 text-slate-500")}>
              <Clock size={11} aria-hidden /> {time}
            </span>
          )}
          {entry.recurrence && (
            <span className={cn(TYPE.footnote, "inline-flex items-center gap-1 text-slate-500")}>
              <Repeat size={11} aria-hidden /> {entry.recurrence}
            </span>
          )}
          {entry.category && (
            <Chip tone={categoryTone(entry.category)} size="xs" icon={<Icon size={10} aria-hidden />}>
              {entry.category}
            </Chip>
          )}
        </div>
      </div>
      {priceLabel && (
        <span className={cn(TYPE.caption, "shrink-0", entry.isFree ? "text-emerald-700" : "text-slate-600")}>
          {priceLabel}
        </span>
      )}
    </Link>
  );
}

export { DateTile };
